/**
 * Helpers puros da tela de Configurações de Solicitações: definição dos
 * quatro catálogos, parsing das faixas de idade e validação das regras de
 * prazo. Sem React e sem request.
 */

export const CATALOGOS = {
  demandas: {
    titulo: "Demandas", singular: "demanda",
    hint: "O que se pede (peça refeita, peça adicional, amostra...).",
  },
  ncs: {
    titulo: "Não conformidades", singular: "não conformidade",
    hint: "O sintoma visto na obra ou na fábrica, sem prender ao processo.",
  },
  processos: {
    titulo: "Processos de origem", singular: "processo de origem",
    hint: "Onde o problema nasceu (projeto, PCP, fábrica, cliente...).",
  },
  causas: {
    titulo: "Causas-raiz", singular: "causa-raiz",
    hint: "Por que aconteceu — cada causa numa categoria 6M da Produção.",
  },
};

export const ORDEM_CATALOGOS = ["demandas", "ncs", "processos", "causas"];

/** Código estável a partir do que a pessoa digitou: MAIÚSCULAS_COM_UNDERLINE,
 * sem acento (o backend normaliza igual; aqui é só para mostrar antes). */
export function normalizarCodigo(texto) {
  return String(texto || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toUpperCase().trim()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** "2, 5; 10 20" -> [2, 5, 10, 20] (positivos, únicos, em ordem).
 * Devolve `{ faixas, erro }`. */
export function parseFaixas(texto) {
  const partes = String(texto || "").split(/[\s,;]+/).filter(Boolean);
  if (partes.length === 0) return { faixas: [], erro: "Informe ao menos uma faixa (ex.: 2, 5, 10, 20)." };
  const numeros = partes.map((p) => Number(p));
  if (numeros.some((n) => !Number.isInteger(n) || n <= 0)) {
    return { faixas: [], erro: "Faixas devem ser dias úteis inteiros e positivos." };
  }
  return { faixas: [...new Set(numeros)].sort((a, b) => a - b), erro: "" };
}

/** Rótulos das faixas como o painel mostra: "0–2", "2–5", ..., "> 20". */
export function rotulosDasFaixas(faixas) {
  const limites = [0, ...(faixas || [])];
  return limites.map((l, i) => (i + 1 < limites.length ? `${l}–${limites[i + 1]}` : `> ${l}`));
}

/** Valida as regras de prazo antes do PUT. Devolve a 1ª mensagem ou "". */
export function validarRegrasPrazo(regras) {
  for (const r of regras || []) {
    const dias = Number(r.dias_uteis_entrega);
    const folga = Number(r.folga_setor_dias_uteis);
    const nome = r.rota_display || r.rota;
    if (!Number.isInteger(dias) || dias <= 0) return `${nome}: o prazo precisa ser um número inteiro de dias úteis.`;
    if (!Number.isInteger(folga) || folga < 0) return `${nome}: a folga do setor precisa ser um inteiro (0 ou mais).`;
    if (folga >= dias) return `${nome}: a folga do setor precisa ser menor que o prazo de entrega.`;
  }
  return "";
}

/** Junta regras salvas com as rotas conhecidas (rota sem regra herda o padrão). */
export function regrasCompletas({ regras = [], rotas = [], padrao = [] } = {}) {
  const salvas = Object.fromEntries(regras.map((r) => [r.rota, r]));
  const padroes = Object.fromEntries(padrao.map((r) => [r.rota, r]));
  return rotas.map(({ codigo, nome }) => {
    const base = salvas[codigo] || padroes[codigo] || {};
    return {
      rota: codigo,
      rota_display: nome,
      dias_uteis_entrega: base.dias_uteis_entrega ?? "",
      folga_setor_dias_uteis: base.folga_setor_dias_uteis ?? 1,
      ativo: base.ativo ?? true,
    };
  });
}

/**
 * Limiares (dias úteis) que o Painel de Assistências usa para apontar o que
 * passou do tempo. Cada um tem leitor no painel — sem leitor, não entra aqui.
 */
export const LIMIARES = [
  {
    campo: "limiar_autorizacao_du", rotulo: "Autorizar em até (dias úteis)", minimo: 0,
    dica: "Acima disso, a solicitação conta como \"aguardando além do limite\".",
  },
  {
    campo: "limiar_sem_lote_du", rotulo: "Peça nova em lote em até (dias úteis)", minimo: 0,
    dica: "Peça nova autorizada há mais tempo que isso sem lote de corte aparece como atrasada no Backlog.",
  },
  {
    campo: "limiar_sem_separacao_du", rotulo: "Separar requisição em até (dias úteis)", minimo: 0,
    dica: "Requisição autorizada há mais tempo que isso sem separação aparece como atrasada no Backlog.",
  },
  {
    campo: "dias_uteis_para_apurar_causa", rotulo: "Apurar a causa-raiz em até (dias úteis)", minimo: 1,
    dica: "Item classificado sem causa-raiz depois desse prazo aparece como pendente na Qualidade.",
  },
  {
    campo: "ondas_alerta", rotulo: "Obra com muitas ondas a partir de", minimo: 2,
    dica: "Onda é cada dia diferente em que a obra pediu assistência. A Reincidência destaca quem chegar a esse número.",
  },
];

/** Converte os limiares digitados em inteiros. Devolve `{ valores, erro }`. */
export function validarLimiares(cfg) {
  const valores = {};
  for (const { campo, rotulo, minimo } of LIMIARES) {
    const n = Number(cfg?.[campo]);
    if (cfg?.[campo] === "" || !Number.isInteger(n) || n < minimo) {
      return { valores: {}, erro: `${rotulo}: informe um número inteiro a partir de ${minimo}.` };
    }
    valores[campo] = n;
  }
  return { valores, erro: "" };
}
